import { apiRequest } from '../../../shared/services/apiClient';
import {
  CreateCommentAutomationRuleRequest,
  CommentAutomationContentOptionsQuery,
  CommentAutomationContentOptionsResult,
  CommentAutomationListQuery,
  CommentAutomationRule,
  PagedCommentAutomationResult,
  UpdateCommentAutomationRuleRequest,
  normalizeCommentAutomationContentOptions,
  normalizeCommentAutomationRule,
  normalizePagedCommentAutomations,
} from '../types/CommentAutomation';

const basePath = '/api/v1/comment-automations';

const buildQuery = (query: CommentAutomationListQuery = {}): string => {
  const params = new URLSearchParams();
  if (query.channel) {
    params.set('channel', query.channel);
  }
  if (typeof query.isEnabled === 'boolean') {
    params.set('isEnabled', String(query.isEnabled));
  }
  params.set('page', String(query.page && query.page > 0 ? query.page : 1));
  params.set('pageSize', String(query.pageSize && query.pageSize > 0 ? query.pageSize : 50));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const buildContentOptionsQuery = (query: CommentAutomationContentOptionsQuery): string => {
  const params = new URLSearchParams();
  params.set('channel', query.channel);
  if (query.channelAccountId) {
    params.set('channelAccountId', query.channelAccountId);
  }
  if (query.limit && query.limit > 0) {
    params.set('limit', String(query.limit));
  }
  if (query.after) {
    params.set('after', query.after);
  }
  return `?${params.toString()}`;
};

export const listCommentAutomations = async (
  query: CommentAutomationListQuery = {},
): Promise<PagedCommentAutomationResult> => {
  const payload = await apiRequest<any>(`${basePath}${buildQuery(query)}`);
  return normalizePagedCommentAutomations(payload);
};

export const listCommentAutomationContentOptions = async (
  query: CommentAutomationContentOptionsQuery,
): Promise<CommentAutomationContentOptionsResult> => {
  const payload = await apiRequest<any>(`${basePath}/content-options${buildContentOptionsQuery(query)}`);
  return normalizeCommentAutomationContentOptions(payload);
};

export const getCommentAutomation = async (ruleId: string): Promise<CommentAutomationRule> => {
  const payload = await apiRequest<any>(`${basePath}/${encodeURIComponent(ruleId)}`);
  return normalizeCommentAutomationRule(payload);
};

export const createCommentAutomation = async (
  request: CreateCommentAutomationRuleRequest,
): Promise<CommentAutomationRule> => {
  const payload = await apiRequest<any>(basePath, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return normalizeCommentAutomationRule(payload);
};

export const updateCommentAutomation = async (
  ruleId: string,
  request: UpdateCommentAutomationRuleRequest,
): Promise<CommentAutomationRule> => {
  const payload = await apiRequest<any>(`${basePath}/${encodeURIComponent(ruleId)}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
  return normalizeCommentAutomationRule(payload);
};

export const setCommentAutomationEnabled = async (
  ruleId: string,
  isEnabled: boolean,
): Promise<CommentAutomationRule> => {
  const payload = await apiRequest<any>(`${basePath}/${encodeURIComponent(ruleId)}/enabled`, {
    method: 'PATCH',
    body: JSON.stringify({ isEnabled }),
  });
  return normalizeCommentAutomationRule(payload);
};

export const deleteCommentAutomation = async (ruleId: string): Promise<void> => {
  await apiRequest<void>(`${basePath}/${encodeURIComponent(ruleId)}`, {
    method: 'DELETE',
  });
};
